import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPointOfSale } from '../../shared/models/core/point-of-sale';
import { IPagedResult } from '../../shared/models/paged-result';
import { User } from '../../shared/models/identity/user';
import { Role } from '../../shared/models/identity/role';
import { Permission } from '../../shared/models/identity/permission';
import { IMarket } from '../../shared/models/core/market';
import { BaseFilterSet } from '../../shared/models/base-filter-set';
import { serialize } from '../../utility/serialize';
import { IRoleFilterSet } from '../../shared/models/identity/role-filter-set';

export type CreateUserPayload = Omit<User, 'id' | 'created' | 'user' | 'status'>;

export interface CreateRolePayload {
  name: string;
  permissions: string[];
  organizationId?: string;
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery,
  tagTypes: ['Users', 'Roles', 'Permissions', 'Markets'],
  endpoints: (builder) => ({
    myPointSales: builder.query<IPointOfSale[], void>({
      query: () => ({
        url: `/api/users/my/pointofsales`,
        method: 'GET',
      }),
    }),
    userPaged: builder.query<IPagedResult<User>, string>({
      query: (params) => ({
        url: `/api/users?${params}`,
        method: 'GET',
        params: {
          size: 1000,
        },
      }),
    }),

    // Users
    getUsers: builder.query<IPagedResult<User>, BaseFilterSet>({
      query: (filterSet) => ({
        url: `/api/users?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    getUser: builder.query<User, string>({
      query: (id) => `/api/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    addUser: builder.mutation<User, CreateUserPayload>({
      query: (body) => ({
        url: '/api/users',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: builder.mutation<User, Partial<User> & Pick<User, 'id'>>({
      query: (data) => ({
        url: `/api/users/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Users', id },
        { type: 'Users', id: 'LIST' },
      ],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    deactivateUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/users/${id}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Users', id },
        { type: 'Users', id: 'LIST' },
      ],
    }),
    changePasswordByAdmin: builder.mutation<
      void,
      { userId: string; newPassword: string; confirmNewPassword: string }
    >({
      query: ({ userId, newPassword, confirmNewPassword }) => ({
        url: `/api/users/${userId}/:changePasswordByAdmin`,
        method: 'POST',
        body: {
          newPassword,
          confirmNewPassword,
        },
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'Users', id: userId }],
    }),
    changePassword: builder.mutation<
      void,
      { userId: string; oldPassword: string; newPassword: string; confirmNewPassword: string }
    >({
      query: ({ userId, oldPassword, newPassword, confirmNewPassword }) => ({
        url: `/api/users/${userId}/:changePassword`,
        method: 'POST',
        body: {
          oldPassword,
          newPassword,
          confirmNewPassword,
        },
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'Users', id: userId }],
    }),
    forgotPassword: builder.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: '/api/users/:forgotPassword',
        method: 'POST',
        body: {
          email,
        },
      }),
    }),
    resetPassword: builder.mutation<void, { userId: string; token: string; newPassword: string }>({
      query: ({ userId, token, newPassword }) => ({
        url: `/api/users/${userId}/:resetPassword`,
        method: 'POST',
        body: {
          token,
          newPassword,
        },
      }),
    }),

    // Roles
    getRoles: builder.query<IPagedResult<Role>, IRoleFilterSet>({
      query: (filterSet) => `/api/roles?${serialize(filterSet)}`,
      providesTags: ['Roles'],
    }),

    getRole: builder.query<Role, string>({
      query: (id) => `/api/roles/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Roles', id }],
    }),

    addRole: builder.mutation<Role, CreateRolePayload>({
      query: (role) => ({
        url: '/api/roles',
        method: 'POST',
        body: role,
      }),
      invalidatesTags: ['Roles'],
    }),

    updateRole: builder.mutation<Role, CreateRolePayload & { id: string }>({
      query: (data) => ({
        url: `/api/roles/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Roles', id }],
    }),

    deleteRole: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/roles?ids=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Roles'],
    }),

    // Permissions
    getPermissions: builder.query<Permission[], void>({
      query: () => '/api/permissions',
      providesTags: ['Permissions'],
    }),

    // Markets
    getMarkets: builder.query<IPagedResult<IMarket>, void>({
      query: () => '/api/markets',
      providesTags: ['Markets'],
    }),

    uploadProfileImage: builder.mutation<{ tempKey: string }, FormData>({
      query: (values) => ({
        url: `/api/uploader`,
        method: 'POST',
        body: values,
      }),
    }),

    updateProfileImage: builder.mutation<
      void,
      {
        userId: string;
        imageTempKey: string;
        imageFileName: string;
        userProfileImageFileName: string;
      }
    >({
      query: ({ userId, imageTempKey, imageFileName, userProfileImageFileName }) => ({
        url: `/api/users/${userId}`,
        method: 'PUT',
        body: {
          ImageTempKey: imageTempKey,
          ImageFileName: imageFileName,
          UserProfileImageFileName: userProfileImageFileName,
        },
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'Users', id: userId }],
    }),
    sellers: builder.query<User[], string>({
      query: (params) => ({
        url: `/api/users/sellers?${params}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useMyPointSalesQuery,
  useLazyMyPointSalesQuery,
  useLazyUserPagedQuery,
  useGetUserQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useUserPagedQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useDeactivateUserMutation,
  useChangePasswordByAdminMutation,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLazyGetRolesQuery,
  useGetRoleQuery,
  useLazyGetRoleQuery,
  useAddRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useGetMarketsQuery,
  useUploadProfileImageMutation,
  useUpdateProfileImageMutation,
  useSellersQuery,
  useLazySellersQuery,
} = userApi;
